// stripe.js
// Utility functions for Stripe checkout via Supabase Edge Functions

import { supabase } from '../supabase';

// Valid choices
const VALID_PLANS = ['starter', 'pro', 'ultimate'];
const VALID_INTERVALS = ['month']; // support year later if/when you add yearly prices

// Ensure user is signed in (and try a silent refresh once)
const ensureSession = async () => {
  let { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    session = refreshed?.session ?? null;
  }
  if (!session) throw new Error('You must be signed in to subscribe.');
  return session;
};

/**
 * Initiate a checkout session with a specific plan/interval.
 * @param {'starter'|'pro'|'ultimate'} plan
 * @param {'month'|'year'} interval
 * @returns {Promise<string>} Stripe Checkout URL
 */
export const initiateCheckout = async (plan = 'pro', interval = 'month') => {
  if (!VALID_PLANS.includes(plan)) throw new Error(`Invalid plan: ${plan}`);
  if (!VALID_INTERVALS.includes(interval)) throw new Error(`Invalid interval: ${interval}`);

  await ensureSession();

  // Call the Edge Function (adds apikey + Authorization headers for you)
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { plan, interval },
  });

  if (error) {
    console.error('create-checkout-session error:', error);
    throw new Error(error.message || 'Subscription error');
  }

  const url = data?.url;
  if (!url) throw new Error('No checkout URL returned from server.');
  return url;
};

/**
 * Redirect to Stripe Checkout
 */
export const redirectToCheckout = async (plan = 'pro', interval = 'month') => {
  const url = await initiateCheckout(plan, interval);
  window.location.assign(url);
};

/**
 * Open the Stripe Billing Portal for the current user
 */
export const openBillingPortal = async () => {
  await ensureSession();
  const { data, error } = await supabase.functions.invoke('create-billingportal-session');
  if (error) {
    console.error('create-billingportal-session error:', error);
    throw new Error(error.message || 'Could not open billing portal');
  }
  if (!data?.url) throw new Error('No billing portal URL returned from server.');
  window.location.assign(data.url);
};